import MapBoxMap from '../../../../components/mapboxmap/MapBoxMap';
import useAppSelector from '../../../../core/hooks/useAppSelector';
import { selectZoomLevel } from '../../../itinerary/MapSlice';
import { Agency, OohRoute } from '../../../../core/graphql/types';
import { useMemo, useState } from 'react';
import { Layer, Marker, Popup, Source } from 'react-map-gl';
import { BoxIcon, WarehouseIcon } from '../../../../components/svgs';
import useUrl from '../../../../core/hooks/useUrl';
import OohPopupContent, { OohPopupContentProps } from './OohPopupContent';
import { CourierPosition } from '../../../../core/grpc/trafficUpdates_pb';
import useLiveTrackingPolyline from '../../../../core/hooks/useLiveTrackingPolyline';
import { courierLineLayer } from '../../../../components/mapSection/components/polylines/LineLayer';
import { courierLayer } from '../../../../components/mapSection/components/polylines/ArrowLayer';
import PulsatingDot from '../../../../components/pulsatingDot/PulsatingDot';

export interface OohMapProps {
  oohRoutes: OohRoute[];
  agency: Agency | undefined;
  liveTrackingCoordinates: CourierPosition[];
}

const OohMap = ({
  oohRoutes,
  agency,
  liveTrackingCoordinates,
}: OohMapProps) => {
  const mapZoomLevel = useAppSelector(selectZoomLevel);
  const { paramValue: routeParamValue } = useUrl<string>('Route');
  const [popupInfo, setPopupInfo] = useState<OohPopupContentProps | null>(null);
  const { liveTrackingPolyline, lastKnownLiveTrackingPosition } =
    useLiveTrackingPolyline(liveTrackingCoordinates);

  const oohPoints = useMemo(() => {
    const uniquePointsMap = new Map<
      string,
      { oohPoint: any; routeId: string }
    >();

    oohRoutes.forEach((route) => {
      route.oohPoints.forEach((oohPoint) => {
        const key = `${oohPoint.latitude}-${oohPoint.longitude}`;

        const existing = uniquePointsMap.get(key);

        if (
          !existing ||
          (route.id === routeParamValue && existing.routeId !== routeParamValue)
        ) {
          uniquePointsMap.set(key, { oohPoint, routeId: route.id });
        }
      });
    });

    return Array.from(uniquePointsMap.values()).map(({ oohPoint, routeId }) => (
      <Marker
        key={`marker-${routeId}-${oohPoint.id}`}
        longitude={oohPoint.longitude}
        latitude={oohPoint.latitude}
        anchor="bottom"
        onClick={(e) => {
          e.originalEvent.stopPropagation();
          setPopupInfo({ oohPoint });
        }}
      >
        <div
          className="flex flex-col items-center"
          title={
            routeId !== routeParamValue
              ? `${oohPoint.name} - OOH Point not part of the route`
              : `${oohPoint.name}`
          }
        >
          <div
            className={`flex items-center justify-center rounded-full ${
              routeId === routeParamValue ? 'bg-red' : 'bg-dots-grey'
            } p-2`}
          >
            <BoxIcon className="h-5 w-5 fill-white text-white" />
          </div>
          <div className="border-t-6 border-t-red-600 h-0 w-0 border-l-4 border-r-4 border-l-transparent border-r-transparent"></div>
        </div>
      </Marker>
    ));
  }, [oohRoutes, routeParamValue]);

  const agencyMarker = useMemo(() => {
    if (agency) {
      return (
        <Marker
          key={`agency-${agency.id}`}
          longitude={agency.longitude}
          latitude={agency.latitude}
          anchor="bottom"
        >
          <div className="flex flex-col items-center" title={agency.name!}>
            <div
              className={`flex items-center justify-center rounded-full bg-design-green p-2`}
            >
              <WarehouseIcon className="h-5 w-5 fill-white text-white" />
            </div>
            <div className="border-t-6 border-t-red-600 h-0 w-0 border-l-4 border-r-4 border-l-transparent border-r-transparent"></div>
          </div>
        </Marker>
      );
    }
  }, [agency]);

  return (
    <MapBoxMap
      id="oohMap"
      zoomLevel={mapZoomLevel}
      interactiveLayerIds={undefined}
    >
      {agencyMarker}
      {oohPoints}
      {popupInfo && (
        <Popup
          anchor="bottom"
          latitude={Number(popupInfo.oohPoint.latitude)}
          longitude={Number(popupInfo.oohPoint.longitude)}
          onClose={() => setPopupInfo(null)}
          className="text-black"
          maxWidth="none"
          offset={30}
        >
          <OohPopupContent oohPoint={popupInfo.oohPoint} />
        </Popup>
      )}

      {liveTrackingCoordinates.length > 0 && (
        <>
          <Source type="geojson" data={liveTrackingPolyline}>
            <Layer {...courierLineLayer} />
            <Layer {...courierLayer} />
          </Source>
          {lastKnownLiveTrackingPosition && (
            <Marker
              anchor="center"
              longitude={lastKnownLiveTrackingPosition.longitude}
              latitude={lastKnownLiveTrackingPosition.latitude}
            >
              <PulsatingDot title="Courier position" />
            </Marker>
          )}
        </>
      )}
    </MapBoxMap>
  );
};

export default OohMap;
